import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Button,
  FormFeedback,
  CustomInput,
  Spinner,
  Table,
} from "reactstrap";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";

//import images
import usePersistStoreData from "../../../Components/Hooks/usePersistStoreData";
import { ProfileDetailThunk } from "../../../slices/auth/login/thunk";
import { useDispatch } from "react-redux";
import { maskAccountNumber } from "../../../Components/Hooks/MaskText";
import { useFormik, Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import EsignConfirmationModal from "../../../Components/Modals/EsignConfirmationModal";
import CommonLoading from "../../../Components/Common/CommonLoading";
import {
  reKycPanUtilityThunk,
  reKycValidatePersonalDetailsThunk,
  reKyc_UpdatePersonalDetailsThunk,
  reKyc_digio_requestResponseThunk,
  reKyc_digio_requestThunk,
} from "../../../slices/thunks";
import { useSelector } from "react-redux";
import { showToast } from "../../../Components/ToastNotification";

import EditNameModal from "../../../Components/Modals/EditNameModal";
import EditEmailMobileModal from "../../../Components/Modals/EditEmailMobileModal";
import EditDobModal from "../../../Components/Modals/EditDobModal";
import EditMaritalStatusModal from "../../../Components/Modals/EditMaritalStatusModal";
import EditGenderModal from "../../../Components/Modals/EditGenderModal";
import ConfirmationModal from "../../../Components/Modals/ConfirmationModal";
import IpvDetails from "./IpvDetails";
import DocumentsUpload from "./DocumentsUpload";
import E_Signature from "./E_Signature";
import OtherDetails from "./OtherDetails";
import BankDetails from "./BankDetails";
import ReKycStepForm from "./ReKycStepForm";
import { resetReKycAddBankData } from "../../../slices/Re_kyc/AddBank/reducer";
import { resetAll, resetStore } from "../../../store";
import Message from "../../../Components/Common/Message";

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userData } = usePersistStoreData();

  // state to send in bank details
  const newPrimary = userData?.personal_details?.new_details?.banking?.find(
    (b) => b.is_primary
  );

  const oldPrimary = userData?.banking?.find((b) => b.is_primary);

  const [selectedPrimary, setSelectedPrimary] = useState(
    newPrimary ?? oldPrimary ?? {}
  );

  const token = userData?.token;
  const [steps, setSteps] = useState(userData?.rekyc_progress + 1);

  const {
    reKyc_digio_requestResponseData,
    reKyc_digio_requestResponseLoading,
  } = useSelector((state) => state.Re_Kyc_Digio_Request_Response);

  const { reKyc_UpdatePersonalDetailData, reKyc_UpdatePersonalDetailLoading } =
    useSelector((state) => state.Re_Kyc_Update_Personal_Details);

  // states for validate api
  const { reKyc_validateDetailsData, reKyc_validateDetailsLoading } =
    useSelector((state) => state.ReKycValidateDetails);

  // states for pan utility
  const { reKycPanUtilityData, reKycPanUtilityLoading } = useSelector(
    (state) => state.ReKycPanUtility
  );
  const [changeName, setChangeName] = useState(
    // reKyc_digio_requestResponseData?.full_name ??
    reKyc_UpdatePersonalDetailData?.name ??
      userData?.personal_details?.new_details?.name ??
      userData?.personal_details?.full_name ??
      ""
  );
  const initialName =
    reKyc_UpdatePersonalDetailData?.name ??
    userData?.personal_details?.new_details?.name ??
    userData?.personal_details?.full_name ??
    "";

  const [changeDob, setChangeDob] = useState(
    reKyc_UpdatePersonalDetailData?.dob ??
      userData?.personal_details?.new_details?.dob ??
      userData?.personal_details?.dob ??
      ""
  );

  const initialDob =
    reKyc_UpdatePersonalDetailData?.dob ??
    userData?.personal_details?.new_details?.dob ??
    userData?.personal_details?.dob ??
    "";

  const [errors, setErrors] = useState({
    maritalStatus: "",
    phone: "",
    email: "",
    digioVerify: "",
    name: "",
    dob: "",
  });

  const derivedErrors = {
    name: "",
    phone: "",
    email: "",
    digioVerify: "",
    dob: "",
  };

  const isReKycInProgress = userData?.rekyc_progress === 0;
  const shouldSkipAllErrors =
    isReKycInProgress && reKyc_UpdatePersonalDetailData?.rekyc_progress >= 1;

  // phone verify error conditions
  const isPhoneUnverified =
    userData?.is_phone_verified !== 1 &&
    reKyc_UpdatePersonalDetailData?.is_phone_verified !== 1 &&
    !reKyc_validateDetailsData?.is_phone_matched;

  // email verify error conditions
  const isEmailUnverified =
    userData?.is_email_verified !== 1 &&
    reKyc_UpdatePersonalDetailData?.is_email_verified !== 1 &&
    !reKyc_validateDetailsData?.is_email_matched;

  // DOB verify error conditions
  const isDobUnverified =
    !reKycPanUtilityData?.is_dob_matched &&
    !reKyc_validateDetailsData?.is_dob_correct;
  const hasDobChanged = changeDob !== initialDob;

  // name verify error conditions
  const isNameUnverified =
    !reKycPanUtilityData?.is_name_matched &&
    !reKyc_validateDetailsData?.is_name_correct;
  const hasNameChanged = changeName !== initialName;
  // digio verify error conditions
  const isDigioUnverified =
    reKyc_digio_requestResponseData?.is_digilocker_connected !== 1 &&
    reKyc_validateDetailsData?.is_digilocker_connect;

  // derive errors using conditions
  if (
    isReKycInProgress &&
    !shouldSkipAllErrors &&
    !reKyc_UpdatePersonalDetailLoading &&
    !reKycPanUtilityLoading
  ) {
    if (isPhoneUnverified) {
      derivedErrors.phone = "Please verify your phone";
    }
    if (isDigioUnverified) {
      derivedErrors.digioVerify = "Please verify your details";
    }
    if (isDobUnverified) {
      if (!hasDobChanged) {
        derivedErrors.dob = "Please enter your correct dob";
      }
    }
    if (isEmailUnverified) {
      derivedErrors.email = "Please verify your email";
    }

    if (isNameUnverified) {
      // Only show error if name hasn't been changed yet
      if (!hasNameChanged) {
        derivedErrors.name = "Please enter your correct name";
      }
    }
  }

  const hasAnyError = () => {
    const allErrors = shouldSkipAllErrors
      ? {}
      : { ...errors, ...derivedErrors };

    return Object.values(allErrors).some((v) => Boolean(v));
  };

  const addressData =
    [
      reKyc_digio_requestResponseData?.current_address_line,
      // reKyc_digio_requestResponseData?.current_city,
      // reKyc_digio_requestResponseData?.current_state,
      // reKyc_digio_requestResponseData?.current_zip_code,
    ]
      .filter(Boolean)
      .join(", ") ||
    [
      userData?.personal_details?.new_details?.current_address?.address,
      // userData?.personal_details?.new_details?.current_address?.city,
      // userData?.personal_details?.new_details?.state,
      // userData?.personal_details?.new_details?.pin,
    ]
      .filter(Boolean)
      .join(", ") ||
    [
      userData?.personal_details?.address?.line_one,
      userData?.personal_details?.address?.line_two,
      userData?.personal_details?.address?.line_three,
      userData?.personal_details?.address?.line_four,
    ]
      .filter(Boolean)
      .join(", ") ||
    "";
  
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("1");
  const [passwordShow, setPasswordShow] = useState(false);
  const [micrShow, setMicrShow] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const { reKyc_digio_requestData, reKyc_digio_requestLoading } = useSelector(
    (state) => state.Re_Kyc_Digio_Request
  );

  // states for edit modal for name
  const [edit_name, setEdit_name] = useState(false);
  const close_editName = () => {
    setEdit_name(false);
  };

  // states for edit modal for mobile or email
  const [edit_MailMobile, setEdit_MailMobile] = useState(false);
  const close_editMailMobile = () => {
    setEdit_MailMobile(false);
  };
  const [editMode, setEditMode] = useState("");
  const [modalHeading, setModalHeading] = useState("");
  const openEditModal = (mode) => {
    setEditMode(mode);
    setModalHeading(mode === "email" ? "Edit Email" : "Edit Mobile Number");
    setEdit_MailMobile(true);
  };

  // state for edit modal for dob
  const [edit_dob, setEdit_dob] = useState(false);
  const close_editDob = () => {
    setEdit_dob(false);
  };

  // state for edit modal for marital status
  const [edit_marital, setEdit_marital] = useState(false);
  const close_editMarital = () => {
    setEdit_marital(false);
  };

  // state for edit modal for gender details
  const [edit_gender, setEdit_gender] = useState(false);
  const close_editGender = () => {
    setEdit_gender(false);
  };

  const activeStyle = {
    backgroundColor: "#01256d",
    color: "white !important",
    padding: "0.7rem",

    borderRadius: "5px",
  };

  // options for marital status
  const optionMaritalStatus = [
    { key: 1, label: "Single" },
    { key: 2, label: "Married" },
    { key: 3, label: "Divorced" },
    { key: 4, label: "Widowed" },
    { key: 5, label: "Separated" },
  ];
  const [maritalOption, setMaritalOption] = useState(
    reKyc_digio_requestResponseData?.marital_status ||
      userData?.personal_details?.new_details.marital_status ||
      userData?.personal_details?.marital_status ||
      ""
  );

  const [maritalFile, setMaritalFile] = useState(null);
  const handleMaritalFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (file.type !== "application/pdf") {
        showToast("Only PDF files are allowed", "error");
        e.target.value = ""; // clear input
        setMaritalFile(null);
        return;
      }

      // Check file size
      if (file.size > 2 * 1024 * 1024) {
        // 2MB
        showToast("File size should not exceed 2MB", "error");
        e.target.value = ""; // clear input
        setMaritalFile(null);
        return;
      }

      setMaritalFile(file);
    }
  };

  // update personal details
  const handlePersonalDetails = async () => {
    try {
      // Step 1: Call PAN verification API if progress is 0
      if (isReKycInProgress) {
        const resPan = await dispatch(
          reKycPanUtilityThunk({
            params: {
              name: changeName,
              dob: changeDob,
            },
            token,
          })
        );

        if (resPan?.payload?.code !== 200) {
          const checkName = resPan?.payload?.data?.is_name_matched;
          const checkDob = resPan?.payload?.data?.is_dob_matched;
          showToast(
            resPan?.payload?.message ||
              "Something went wrong, please try again",
            "error"
          );
          if (!checkName) {
            setErrors((prev) => ({
              ...prev,
              name: "Please enter your correct name",
            }));
          }
          if (!checkDob) {
            setErrors((prev) => ({
              ...prev,
              dob: "Please enter your correct dob",
            }));
          }
          return;
        }
      }

      const formData = new FormData();

      if (reKyc_digio_requestResponseData) {
        formData.append("type", "all");

        formData.append(
          "email",
          reKyc_UpdatePersonalDetailData?.email ??
            userData?.personal_details?.new_details?.email ??
            userData?.personal_details?.email
        );
        formData.append(
          "phone",
          reKyc_UpdatePersonalDetailData?.phone ??
            userData?.personal_details?.new_details?.phone ??
            userData?.personal_details?.mobile
        );
        formData.append(
          "name",
          changeName
          // reKyc_digio_requestResponseData?.full_name ??
          //   userData?.personal_details?.new_details?.name ??
          //   userData?.personal_details?.full_name
        );
        formData.append(
          "gender",
          reKyc_digio_requestResponseData?.gender ??
            userData?.personal_details?.new_details?.gender ??
            userData?.personal_details?.gender
        );
        formData.append("dob", changeDob);
        formData.append(
          "pan",
          userData?.personal_details?.new_details?.pan ??
            userData?.personal_details?.pan
        );
        formData.append(
          "marital_status",
          maritalOption ?? userData?.personal_details?.marital_status
        );
        if (maritalFile) {
          formData.append("marriage_certificate", maritalFile);
        }
        formData.append("cr_address", addressData);
        formData.append(
          "cr_city",
          reKyc_digio_requestResponseData?.current_city ??
            userData?.personal_details?.new_details?.current_address?.city ??
            userData?.personal_details?.city
        );
        formData.append(
          "cr_state",
          reKyc_digio_requestResponseData?.current_state ??
            userData?.personal_details?.new_details?.permanent_address?.state ??
            userData?.personal_details?.state
        );
        formData.append("cr_country", "India");
        formData.append(
          "cr_pin",
          reKyc_digio_requestResponseData?.current_zip_code ||
            userData?.personal_details?.new_details?.current_address?.pin ||
            userData?.personal_details?.pincode
        );
        formData.append("pr_address", addressData);
        formData.append(
          "pr_city",
          reKyc_digio_requestResponseData?.permanent_city ??
            userData?.personal_details?.new_details?.permanent_address?.city ??
            userData?.personal_details?.city
        );
        formData.append(
          "pr_state",
          reKyc_digio_requestResponseData?.permanent_state ??
            userData?.personal_details?.new_details?.permanent_address?.state ??
            userData?.personal_details?.state
        );
        formData.append("pr_country", "India");
        formData.append(
          "pr_pin",
          reKyc_digio_requestResponseData?.permanent_zip_code ||
            userData?.personal_details?.new_details?.permanent_address?.pin ||
            userData?.personal_details?.pincode
        );

        if (
          reKyc_digio_requestResponseData?.current_locality ||
          userData?.personal_details?.new_details?.current_address?.locality
        ) {
          formData.append(
            "cr_locality",
            reKyc_digio_requestResponseData?.current_locality ||
              userData?.personal_details?.new_details?.current_address?.locality
          );
        }
        if (
          reKyc_digio_requestResponseData?.permanent_locality ||
          userData?.personal_details?.new_details?.permanent_address?.locality
        ) {
          formData.append(
            "pr_locality",
            reKyc_digio_requestResponseData?.permanent_locality ||
              userData?.personal_details?.new_details?.permanent_address
                ?.locality
          );
        }

        if (
          reKyc_digio_requestResponseData?.aadhaar_number ||
          userData?.personal_details?.new_details?.aadhaar
        ) {
          formData.append(
            "aadhaar",
            reKyc_digio_requestResponseData?.aadhaar_number ||
              userData?.personal_details?.new_details?.aadhaar
          );
        }
      }

      const resUpdate = await dispatch(
        reKyc_UpdatePersonalDetailsThunk({ body: formData, token })
      );

      if (resUpdate?.payload?.code === 200) {
        showToast(
          resUpdate?.payload?.message ||
            "Personal Details Updated Successfully",
          "success"
        );
        setSteps((prev) => prev + 1);
      } else {
        showToast(
          resUpdate?.payload?.message ||
            "Something went wrong, please try again",
          "error"
        );
      }
    } catch (err) {
      showToast(
        err?.message || "Something went wrong, please try again",
        "error"
      );
    }
  };

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Open Digio popup with API response data
  const openDigioPopup = (digioData) => {
    if (!window.Digio) {
      // console.error("Digio SDK not loaded");
      showToast(
        "Digio SDK is not loaded. Please refresh the page and try again.",
        "error"
      );
      setIsProcessing(false);
      return;
    }
    try {
      // Digio configuration with callback
      const options = {
        environment: "production", // or "sandbox" for testing
        callback: function (response) {
          if (response.hasOwnProperty("error_code")) {
            // console.log("Error occurred in process:", response);
            // Handle error
            setIsProcessing(false);
            showToast(response?.message, "error");
            return;
          }
          // console.log("Signing completed successfully:", response);
          dispatch(
            reKyc_digio_requestResponseThunk({
              params: {
                request_id: digioData?.request_id,
                type: steps == 4 ? "ipv" : "re-kyc",
              },
              token,
            })
          )
            .then((res) => {
              if (res?.payload?.code === 200) {
                showToast(
                  res?.payload?.message || "Request successful",
                  "success"
                );
              } else {
                showToast(
                  res?.error?.message ||
                    "Something went wrong, please try again",
                  "error"
                );
                setIsProcessing(false);
              }
            })
            .catch((err) => {
              showToast(
                err?.message || "Something went wrong, please try again",
                "error"
              );
              setIsProcessing(false);
            })
            .finally(() => {
              setIsProcessing(false);
            });
        },
        event_listener: function () {
          // Prevent Digio SDK from throwing when trying to call this
          // console.log("Digio event listener triggered");
          setIsProcessing(false);
        },
      };

      // Create new Digio instance
      const digio = new window.Digio(options);

      // Initialize Digio
      digio.init();
      digio.submit(
        digioData?.request_id,
        digioData?.customer_identifier,
        digioData?.access_token
      );
    } catch (error) {
      // console.error("Error opening Digio popup:", error);
      showToast("Error opening Digio: " + error.message, "error");
      setIsProcessing(false);
    }
  };

  const handleDigioRequest = () => {
    if (isProcessing) return; // just in case
    setIsProcessing(true);
    dispatch(
      reKyc_digio_requestThunk({ params: { request_type: "re-kyc" }, token })
    )
      .then((res) => {
        if (res?.payload?.code === 200) {
          openDigioPopup(res?.payload?.data);
        } else {
          showToast(
            res?.error?.message || "Something went wrong, please try again",
            "error"
          );
          setIsProcessing(false);
        }
      })
      .catch((err) => {
        showToast(
          err?.message || "Something went wrong, please try again",
          "error"
        );
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    dispatch(ProfileDetailThunk(userData?.token))
      .then((res) => {
        setLoading(false);
        dispatch(resetReKycAddBankData());
      })
      .catch((err) => {
        setLoading(false);
      });
  }, [userData?.token]);

  useEffect(() => {
    if (location?.state?.tab) {
      tabChange(location?.state?.tab);
    }
  }, []);

  // validate user details api
  useEffect(() => {
    if (!isReKycInProgress) return;
    dispatch(reKycValidatePersonalDetailsThunk({ token }))
      .then((res) => {
        if (res?.payload?.code === 200) {
        } else {
          navigate("/edit_option");
          showToast(
            res?.error?.message || "Something went wrong, please try again",
            "error"
          );
        }
      })
      .catch((err) => {
        showToast(
          err?.message || "Something went wrong, please try again",
          "error"
        );
      });
  }, []);

  useEffect(() => {
    return () => {
      // CLEAN UP ON UNMOUNT
      dispatch(resetAll());
    };
  }, []);
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {loading || reKyc_validateDetailsLoading ? (
            <div
              className="centerContent h-100 w-100"
              style={{ minHeight: "80vh" }}
            >
              <CommonLoading />
            </div>
          ) : (
            <>
              <div className="position-relative mx-n4 mt-n4">
                <div
                  className="profile-wid-bg profile-setting-img"
                  style={{ backgroundColor: "#01256D" }}
                >
                  <div className="overlay-content">
                    <div className="text-end p-3">
                      <div className="p-0 ps-2 ms-auto rounded-circle profile-photo-edit">
                        <Nav
                          pills
                          className=" animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
                          role="tablist"
                        >
                          <NavItem>
                            <NavLink
                              // className={classnames(" ", {
                              //   active: activeTab === "1",
                              // })}
                              onClick={() => {
                                navigate("/edit_option");
                              }}
                              style={{
                                backgroundColor: "transparent",
                                border: "1px solid #0FA67B",
                              }}
                            >
                              <svg
                                className="me-2"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3.99 9L16 9L16 7L3.99 7L3.99 4L3.49691e-07 8L3.99 12L3.99 9Z"
                                  fill="#0FA67B"
                                />
                              </svg>
                              <span className="d-none d-md-inline-block">
                                Go Back
                              </span>
                            </NavLink>
                          </NavItem>
                        </Nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Row>
                <Col xxl={3} xl={4}>
                  <Card className="mt-n5">
                    <CardBody
                      className="p-4"
                      style={{
                        minHeight: "28vh",
                      }}
                    >
                      {activeTab == "1" || activeTab == "0" ? (
                        <div className="text-start mb-1 ">
                          <ReKycStepForm steps={steps} />
                        </div>
                      ) : activeTab == "2" ? (
                        <div className="text-start mb-1 ">
                          <h6
                            className="fs-16 text-white mb-3"
                            style={activeStyle}
                          >
                            Annual Income
                          </h6>
                        </div>
                      ) : activeTab == "3" ? (
                        <div className="text-start mb-1 ">
                          <h6
                            className="fs-16 text-white mb-3"
                            style={activeStyle}
                          >
                            Add Nominee
                          </h6>
                        </div>
                      ) : (
                        <div className="text-start mb-1 ">
                          <h6
                            className="fs-16 text-white mb-3"
                            style={activeStyle}
                          >
                            Add MTF
                          </h6>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col xxl={9} xl={8}>
                  <Card className="mt-xxl-n5 mt-xl-n5">
                    <CardHeader>
                      <Nav
                        className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                        role="tablist"
                      >
                        <NavItem>
                          <NavLink
                            to="#"
                            className={classnames("cursor-pointer", {
                              active: activeTab === "1",
                            })}
                            onClick={() => {
                              tabChange("1");
                            }}
                          >
                            {/* <i className="ri-file-user-line"></i> */}
                            KYC Reactivation and Modification
                          </NavLink>
                        </NavItem>

                        {/* <NavItem>
                          <NavLink
                            to="#"
                            className={classnames("cursor-pointer", {
                              active: activeTab === "2",
                            })}
                            onClick={() => {
                              tabChange("2");
                            }}
                            type="button"
                          >
                            Annual Income
                          </NavLink>
                        </NavItem>
                        */}
                      </Nav>
                    </CardHeader>
                    <CardBody className="p-4">
                      <TabContent activeTab={activeTab}>
                        {/* Edit KYC update/modification */}
                        <TabPane tabId="1">
                          {/* Personal and adress  */}
                          {(steps == 1 || steps == 0) && (
                            <Form>
                              <Row>
                                {userData?.rekcy_appication_status ===
                                  "rejected" && (
                                  <Col lg={12}>
                                    <Message
                                      message={
                                        userData?.rekcy_appication_sreason
                                      }
                                      color="danger"
                                      mb={4}
                                    />
                                  </Col>
                                )}
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <Label
                                        htmlFor="firstnameInput"
                                        className="form-label"
                                      >
                                        Full Name (Username)
                                      </Label>

                                      {/* <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="mb-1"
                                        onClick={() => {
                                          setEdit_name(true);
                                        }}
                                      >
                                        Edit
                                      </Button> */}
                                    </div>

                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="firstnameInput"
                                      placeholder="Enter your full name"
                                      value={changeName}
                                      onChange={(e) => {
                                        setChangeName(e.target.value);
                                        if (errors.name) {
                                          setErrors((prev) => ({
                                            ...prev,
                                            name: "",
                                          }));
                                        }
                                      }}
                                      disabled={
                                        isReKycInProgress &&
                                        !shouldSkipAllErrors
                                          ? reKyc_validateDetailsData?.is_name_correct ===
                                              true ||
                                            reKycPanUtilityData?.is_name_matched ===
                                              true
                                          : true
                                      }
                                    />
                                    {(derivedErrors.name || errors.name) && (
                                      <div className="text-danger">
                                        {derivedErrors.name || errors.name}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <Label
                                        htmlFor="email"
                                        className="form-label"
                                      >
                                        Email
                                      </Label>
                                      <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="mb-1"
                                        onClick={() => openEditModal("email")}
                                      >
                                        {[0, 3].includes(
                                          userData?.trade?.account_status
                                        )
                                          ? "Verify"
                                          : "Edit"}
                                      </Button>
                                    </div>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="email"
                                      placeholder="Enter your email address"
                                      value={
                                        reKyc_UpdatePersonalDetailData?.email ??
                                        userData?.personal_details?.new_details
                                          ?.email ??
                                        userData?.personal_details?.email ??
                                        ""
                                      }
                                      disabled
                                    />
                                    {derivedErrors.email && (
                                      <div className="text-danger">
                                        {derivedErrors.email}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <Label
                                        htmlFor="phonenumberInput"
                                        className="form-label"
                                      >
                                        Mobile Number
                                      </Label>
                                      <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="mb-1"
                                        onClick={() => openEditModal("phone")}
                                      >
                                        {[0, 3].includes(
                                          userData?.trade?.account_status
                                        )
                                          ? "Verify"
                                          : "Edit"}
                                      </Button>
                                    </div>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="phonenumberInput"
                                      placeholder="Enter your mobile number"
                                      value={`+91 ${
                                        reKyc_UpdatePersonalDetailData?.phone ??
                                        userData?.personal_details?.new_details
                                          ?.phone ??
                                        userData?.personal_details?.mobile ??
                                        ""
                                      }`}
                                      disabled
                                    />
                                    {derivedErrors.phone && (
                                      <div className="text-danger">
                                        {derivedErrors.phone}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <Label
                                        htmlFor="DOB"
                                        className="form-label"
                                      >
                                        Date of Birth (DOB)
                                      </Label>
                                      {/* <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="mb-1"
                                        onClick={() => setEdit_dob(true)}
                                      >
                                        Edit
                                      </Button> */}
                                    </div>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="DOB"
                                      placeholder="Enter your DOB"
                                      value={changeDob}
                                      onChange={(e) => {
                                        setChangeDob(e.target.value);
                                        if (errors.dob) {
                                          setErrors((prev) => ({
                                            ...prev,
                                            dob: "",
                                          }));
                                        }
                                      }}
                                      disabled={
                                        isReKycInProgress &&
                                        !shouldSkipAllErrors
                                          ? reKyc_validateDetailsData?.is_dob_correct ===
                                              true ||
                                            reKycPanUtilityData?.is_dob_matched ===
                                              true
                                          : true
                                      }
                                    />
                                    {(derivedErrors.dob || errors.dob) && (
                                      <div className="text-danger">
                                        {derivedErrors.dob || errors.dob}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <Label
                                        htmlFor="Gender"
                                        className="form-label"
                                      >
                                        Gender
                                      </Label>
                                      {/* <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="mb-1"
                                        onClick={() => setEdit_gender(true)}
                                      >
                                        Edit
                                      </Button> */}
                                    </div>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="Gender"
                                      placeholder="Enter your gender"
                                      value={
                                        reKyc_digio_requestResponseData?.gender ??
                                        userData?.personal_details?.new_details
                                          ?.gender ??
                                        userData?.personal_details?.gender ??
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <Label htmlFor="PAN" className="form-label">
                                      PAN
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="PAN"
                                      placeholder="Enter your PAN"
                                      value={
                                        userData?.personal_details?.new_details
                                          ?.pan ??
                                        userData?.personal_details?.pan ??
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={6}>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <Label
                                        htmlFor="maritalOption"
                                        className="form-label"
                                      >
                                        Marital Status
                                      </Label>
                                      {/* <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="mb-1"
                                        onClick={() => setEdit_marital(true)}
                                      >
                                        Edit
                                      </Button> */}
                                    </div>
                                    <Input
                                      type="select"
                                      className="form-control"
                                      id="maritalOption"
                                      placeholder="Enter your marital status"
                                      value={maritalOption}
                                      onChange={(e) => {
                                        setMaritalOption(e.target.value);
                                        if (errors.maritalStatus) {
                                          setErrors((prev) => ({
                                            ...prev,
                                            maritalStatus: "",
                                          }));
                                        }
                                      }}
                                    >
                                      <option value="" disabled>
                                        Select
                                      </option>
                                      {optionMaritalStatus
                                        .filter(
                                          (item) =>
                                            !(
                                              Number(maritalOption) === 2 &&
                                              item.key === 1
                                            )
                                        )
                                        .map((item) => (
                                          <option
                                            value={item.key}
                                            key={item.key}
                                          >
                                            {item.label}
                                          </option>
                                        ))}
                                    </Input>
                                    {errors.maritalStatus && (
                                      <div className="text-danger">
                                        {errors.maritalStatus}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                {maritalOption == "2" && (
                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <Label
                                          htmlFor="maritalFile"
                                          className="form-label"
                                        >
                                          Upload Marriage Certificate
                                        </Label>
                                      </div>
                                      <Input
                                        type="file"
                                        className="form-control"
                                        id="maritalFile"
                                        onChange={handleMaritalFileChange}
                                        accept=".pdf"
                                      ></Input>
                                    </div>
                                  </Col>
                                )}

                                <div className="mb-3">
                                  <span className="badge bg-success p-2 fs-p-sm text-uppercase">
                                    Current Address
                                  </span>
                                </div>
                                <Col lg={12}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="Address"
                                      className="form-label"
                                    >
                                      Address Line
                                    </Label>
                                    <Input
                                      type="textarea"
                                      className="form-control"
                                      id="Address"
                                      placeholder="Enter your Address"
                                      value={addressData}
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="cityInput"
                                      className="form-label"
                                    >
                                      City
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="cityInput"
                                      placeholder="Enter your city"
                                      value={
                                        reKyc_digio_requestResponseData?.current_city ??
                                        userData?.personal_details?.new_details
                                          ?.current_address?.city ??
                                        userData?.personal_details?.city ??
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="StateInput"
                                      className="form-label"
                                    >
                                      State
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="StateInput"
                                      placeholder="Enter your State"
                                      value={
                                        reKyc_digio_requestResponseData?.current_state ??
                                        userData?.personal_details?.new_details
                                          ?.permanent_address?.state ??
                                        userData?.personal_details?.state ??
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="countryInput"
                                      className="form-label"
                                    >
                                      Country
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="countryInput"
                                      placeholder="Country"
                                      value="India"
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="zipcodeInput"
                                      className="form-label"
                                    >
                                      Zip Code
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      minLength="5"
                                      maxLength="6"
                                      id="zipcodeInput"
                                      placeholder="Enter zipcode"
                                      value={
                                        reKyc_digio_requestResponseData?.current_zip_code ||
                                        userData?.personal_details?.new_details
                                          ?.current_address?.pin ||
                                        userData?.personal_details?.pincode ||
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <div className="mb-3">
                                  <span className="badge bg-success p-2 fs-p-sm text-uppercase">
                                    Permanent Address
                                  </span>
                                </div>
                                {/* <div className="mb-2">
                                  <div class="form-check form-check-success">
                                    <Input
                                      class="form-check-input"
                                      type="checkbox"
                                      id="formCheck8"
                                      checked
                                    />
                                    <Label
                                      class="form-check-label"
                                      for="formCheck8"
                                    >
                                      Same as my current address
                                    </Label>
                                  </div>
                                </div> */}
                                <Col lg={12}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="Address2"
                                      className="form-label"
                                    >
                                      Address Line
                                    </Label>
                                    <Input
                                      type="textarea"
                                      className="form-control"
                                      id="Address2"
                                      placeholder="Enter your Address"
                                      value={addressData}
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="cityInput2"
                                      className="form-label"
                                    >
                                      City
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="cityInput2"
                                      placeholder="Enter your city"
                                      value={
                                        reKyc_digio_requestResponseData?.permanent_city ??
                                        userData?.personal_details?.new_details
                                          ?.permanent_address?.city ??
                                        userData?.personal_details?.city ??
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="stateInput2"
                                      className="form-label"
                                    >
                                      State
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="stateInput2"
                                      placeholder="Enter your State"
                                      value={
                                        reKyc_digio_requestResponseData?.permanent_state ??
                                        userData?.personal_details?.new_details
                                          ?.permanent_address?.state ??
                                        userData?.personal_details?.state ??
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="countryInput2"
                                      className="form-label"
                                    >
                                      Country
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      id="countryInput2"
                                      placeholder="Country"
                                      value="India"
                                      disabled
                                    />
                                  </div>
                                </Col>
                                <Col lg={3}>
                                  <div className="mb-3">
                                    <Label
                                      htmlFor="zipcodeInput2"
                                      className="form-label"
                                    >
                                      Zip Code
                                    </Label>
                                    <Input
                                      type="text"
                                      className="form-control"
                                      minLength="5"
                                      maxLength="6"
                                      id="zipcodeInput2"
                                      placeholder="Enter zipcode"
                                      value={
                                        reKyc_digio_requestResponseData?.permanent_zip_code ||
                                        userData?.personal_details?.new_details
                                          ?.permanent_address?.pin ||
                                        userData?.personal_details?.pincode ||
                                        ""
                                      }
                                      disabled
                                    />
                                  </div>
                                </Col>

                                <Col lg={12}>
                                  <div className="hstack gap-2 justify-content-between flex-wrap align-items-start">
                                    {userData?.rekyc_progress === 0 &&
                                      !shouldSkipAllErrors &&
                                      !reKyc_UpdatePersonalDetailLoading &&
                                      !reKycPanUtilityLoading &&
                                      reKyc_digio_requestResponseData?.is_digilocker_connected !==
                                        1 &&
                                      reKyc_validateDetailsData?.is_digilocker_connect && (
                                        <>
                                          <div className="d-flex flex-column">
                                            <button
                                              type="button"
                                              className="btn btn-primary"
                                              style={{
                                                minWidth: "160px",
                                              }}
                                              disabled={
                                                reKyc_digio_requestResponseLoading ||
                                                reKyc_digio_requestLoading ||
                                                isProcessing
                                              }
                                              onClick={handleDigioRequest}
                                            >
                                              {isProcessing ||
                                              reKyc_digio_requestResponseLoading ||
                                              reKyc_digio_requestLoading ? (
                                                <Spinner size="sm" />
                                              ) : (
                                                "Digilocker Verification"
                                              )}
                                            </button>

                                            {derivedErrors.digioVerify && (
                                              <div className="text-danger ">
                                                {derivedErrors.digioVerify}
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}

                                    <button
                                      type="button"
                                      className="btn btn-primary ms-auto"
                                      onClick={() => {
                                        handlePersonalDetails();
                                      }}
                                      disabled={
                                        reKyc_UpdatePersonalDetailLoading ||
                                        reKycPanUtilityLoading ||
                                        hasAnyError()
                                      }
                                      style={{
                                        minWidth: "80px",
                                      }}
                                    >
                                      {reKyc_UpdatePersonalDetailLoading ||
                                      reKycPanUtilityLoading ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        "Next"
                                      )}
                                    </button>
                                  </div>
                                </Col>
                              </Row>
                            </Form>
                          )}

                          {/* Other Information */}
                          {steps == 2 && (
                            <OtherDetails setSteps={setSteps} steps={steps} />
                          )}

                          {/* Bank Details */}
                          {steps == 3 && (
                            <BankDetails
                              setSteps={setSteps}
                              steps={steps}
                              selectedPrimary={selectedPrimary}
                              setSelectedPrimary={setSelectedPrimary}
                            />
                          )}
                          {/* IPV Details */}
                          {steps == 4 && (
                            <IpvDetails setSteps={setSteps} steps={steps} />
                          )}
                          {/* Documents Upload */}
                          {steps == 5 && (
                            <DocumentsUpload
                              setSteps={setSteps}
                              steps={steps}
                            />
                          )}
                          {/* Esignature */}
                          {steps == 6 && (
                            <E_Signature setSteps={setSteps} steps={steps} />
                          )}
                        </TabPane>

                        {/* Annual Income */}
                        {/* <TabPane tabId="2">
                          <AnnualIncome />
                        </TabPane> */}

                        {/* Add Nominee */}
                        {/* <TabPane tabId="3">
                          <AddNominee />
                        </TabPane> */}

                        {/* Add MTF */}
                        {/* <TabPane tabId="4">
                          <AddMtf />
                        </TabPane> */}
                      </TabContent>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>

      {/* modal for edit name  */}
      {edit_name && (
        <EditNameModal edit_name={edit_name} close_editName={close_editName} />
      )}
      {/* modal for edit mail and mobile */}
      {edit_MailMobile && (
        <EditEmailMobileModal
          edit_MailMobile={edit_MailMobile}
          close_editMailMobile={close_editMailMobile}
          mode={editMode}
          heading={modalHeading}
          clearError={(field) =>
            setErrors((prev) => ({ ...prev, [field]: "" }))
          }
        />
      )}
      {/* modal to edit dob */}
      {edit_dob && (
        <EditDobModal edit_dob={edit_dob} close_editDob={close_editDob} />
      )}
      {/* modal to edit marital status */}
      {edit_marital && (
        <EditMaritalStatusModal
          edit_marital={edit_marital}
          close_editMarital={close_editMarital}
        />
      )}
      {/* modal to edit gender details */}
      {edit_gender && (
        <EditGenderModal
          edit_gender={edit_gender}
          close_editGender={close_editGender}
        />
      )}
    </React.Fragment>
  );
};

export default EditProfile;
